import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Share2,
  Star,
  MapPin,
  Phone,
  Mail,
  X,
  Check,
  Clock,
  Loader2,
  Minus,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { getPrivateParkBySlug } from "../data/privateParks";
import Breadcrumbs from "../components/Breadcrumbs";
import JsonLd from "../components/JsonLd";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Photo carousel ----------------------------------------------------
// Touch-enabled gallery; arrows on desktop, dot indicators, photo counter.
// Swipe threshold of 50px to feel native on mobile.
const Carousel = ({ photos, alt }) => {
  const [idx, setIdx] = useState(0);
  const touchStart = useRef(null);
  const last = photos.length - 1;
  const go = (delta) => setIdx((i) => (i + delta + photos.length) % photos.length);

  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
    touchStart.current = null;
  };

  return (
    <div
      data-testid="private-park-carousel"
      className="relative rounded-2xl overflow-hidden bg-[#e5e0d8] aspect-[16/10]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <img
        src={photos[idx]}
        alt={`${alt} — photo ${idx + 1}`}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
        loading={idx === 0 ? "eager" : "lazy"}
        onError={(e) => {
          // Surface broken placeholder URLs without breaking the page.
          e.currentTarget.style.background = "#e5e0d8";
        }}
      />

      {/* Photo counter */}
      <div className="absolute top-3 right-3 bg-black/55 text-white text-xs font-medium px-2.5 py-1 rounded-full backdrop-blur-sm">
        {idx + 1} / {photos.length}
      </div>

      {/* Prev / next */}
      <button
        type="button"
        aria-label="Previous photo"
        data-testid="private-park-carousel-prev"
        onClick={() => go(-1)}
        className="hidden md:flex absolute top-1/2 left-3 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/85 hover:bg-white text-[#2d2a26] shadow-md transition"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        aria-label="Next photo"
        data-testid="private-park-carousel-next"
        onClick={() => go(1)}
        className="hidden md:flex absolute top-1/2 right-3 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/85 hover:bg-white text-[#2d2a26] shadow-md transition"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
        {photos.map((_, i) => (
          <button
            type="button"
            key={i}
            aria-label={`Go to photo ${i + 1}`}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? "w-6 bg-white" : "w-1.5 bg-white/55 hover:bg-white/80"
            }`}
          />
        ))}
      </div>

      {/* Touch hint on mobile only */}
      {last > 0 && (
        <div className="md:hidden absolute bottom-3 right-3 bg-black/55 text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded-full">
          Swipe
        </div>
      )}
    </div>
  );
};

// ---- Reusable stepper --------------------------------------------------
const Stepper = ({ label, value, min, max, onChange, testId }) => (
  <div className="flex items-center justify-between gap-3 py-2.5">
    <span className="text-sm font-medium text-[#2d2a26]">{label}</span>
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-testid={`${testId}-minus`}
        aria-label={`Decrease ${label}`}
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-8 h-8 rounded-full border border-[#e5e0d8] flex items-center justify-center text-[#2d2a26] hover:bg-[#f7f3eb] disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        <Minus className="w-4 h-4" />
      </button>
      <span data-testid={`${testId}-value`} className="w-8 text-center font-semibold tabular-nums">
        {value}
      </span>
      <button
        type="button"
        data-testid={`${testId}-plus`}
        aria-label={`Increase ${label}`}
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
        className="w-8 h-8 rounded-full border border-[#e5e0d8] flex items-center justify-center text-[#2d2a26] hover:bg-[#f7f3eb] disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  </div>
);

// ---- Booking modal -----------------------------------------------------
const BookingModal = ({ listing, dogs, hours, total, onClose }) => {
  const [step, setStep] = useState("form"); // 'form' | 'submitting' | 'done'
  const [form, setForm] = useState({
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    preferred_date: "",
    preferred_time: "",
    dogs,
    hours,
    message: "",
  });

  // Post-booking "Share with friends" handler. Two goals: (1) drive
  // organic growth by getting the guest to invite a friend with their
  // dog to the same booking, and (2) give the host a free demand
  // signal — every share is a potential new booking request.
  //
  // Strategy:
  //   - On mobile / supporting browsers: native navigator.share()
  //     surfaces the iMessage / WhatsApp / Messages picker directly.
  //     Pre-fill `text` with a friendly, specific blurb so the
  //     recipient sees "Just booked X — come with us!" in their
  //     thread preview, not a generic "Check out this URL".
  //   - On desktop / unsupported browsers: fall back to clipboard
  //     copy so power users can paste into Slack / email / IG.
  //
  // GA4: `private_park_booking_shared` fires on every successful
  // share with method ∈ {native, copy_link, abort}. Abort is tracked
  // so we can measure picker-open vs. picker-completed funnels.
  const handleShare = async () => {
    const url = `https://pawground.app/private-parks/${listing.slug}`;
    const priceText = `$${listing.pricing.firstDogPerHour}/hr`;
    const title = `${listing.name} on Pawground`;
    const text =
      `Just booked ${listing.name} — a private fenced yard in ${listing.city}, ${listing.state}. ` +
      `${priceText}, no membership. Come join us with your pup! 🐾`;

    const fire = (method) =>
      track("private_park_booking_shared", {
        listing_slug: listing.slug,
        listing_name: listing.name,
        share_method: method,
      });

    // Native picker path — iMessage / WhatsApp / Mail / Messages.
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title, text, url });
        fire("native");
        return;
      } catch (err) {
        // AbortError = user closed the picker. Don't fall back to
        // clipboard since that'd surprise them with an unexpected
        // toast. Just record the abort and bail.
        if (err?.name === "AbortError") {
          fire("abort");
          return;
        }
        // Other errors (rare — usually a misconfigured browser) →
        // drop through to the clipboard fallback below.
      }
    }

    // Fallback: copy link to clipboard. Same UX as ShareButton's copy
    // path so the toast styling stays consistent across the app.
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Legacy non-secure-context fallback.
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      fire("copy_link");
      toast("Link copied! Paste it anywhere 🐾", {
        duration: 2200,
        position: "bottom-center",
        unstyled: true,
        classNames: {
          toast:
            "rounded-full px-5 py-2.5 text-sm font-body font-semibold shadow-warm-lg",
        },
        style: { backgroundColor: "#1A1A0F", color: "#FFFFFF" },
      });
    } catch {
      toast.error("Couldn't share. Copy the URL from your browser bar.");
    }
  };

  const isValid =
    form.guest_name.trim().length >= 2 &&
    /^.+@.+\..+$/.test(form.guest_email.trim()) &&
    form.preferred_date.trim().length > 0;

  const submit = async () => {
    if (!isValid) return;
    setStep("submitting");
    try {
      const res = await fetch(`${API}/private-park-bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ listing_slug: listing.slug, ...form }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Could not send request");
      }
      setStep("done");
    } catch (e) {
      setStep("form");
      toast.error(e.message || "Couldn't send your request. Please try again.");
    }
  };

  // Lock background scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      data-testid="booking-modal"
      className="fixed inset-0 z-[120] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-modal-title"
        className="bg-[#FAF7F2] rounded-3xl max-w-xl w-full max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#FAF7F2]/95 backdrop-blur-sm border-b border-[#e5e0d8] px-6 py-4 flex items-center justify-between">
          <h2 id="booking-modal-title" className="font-heading text-xl font-bold text-[#2d2a26]">
            Book This Spot
          </h2>
          <button
            type="button"
            data-testid="booking-modal-close"
            onClick={onClose}
            aria-label="Close booking form"
            className="w-9 h-9 rounded-full hover:bg-[#f0ebe1] flex items-center justify-center text-[#6b6560]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === "done" ? (
          <div className="px-6 py-10 text-center">
            <div className="w-14 h-14 rounded-full bg-[#4A7C59]/15 text-[#4A7C59] flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7" strokeWidth={3} />
            </div>
            <h3 className="font-heading text-2xl font-bold text-[#2d2a26] mb-2">Request Sent!</h3>
            <p className="text-[#6b6560] mb-6 leading-relaxed">
              {listing.host.name} will get back to you shortly. You can also reach{" "}
              {listing.host.name} directly:
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center mb-6">
              <a
                href={`tel:${listing.host.phoneRaw}`}
                data-testid="booking-confirmation-phone"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#4A7C59] text-white text-sm font-semibold hover:bg-[#3d6749] transition"
              >
                <Phone className="w-4 h-4" /> {listing.host.phone}
              </a>
              <a
                href={`mailto:${listing.host.email}`}
                data-testid="booking-confirmation-email"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-[#4A7C59] text-[#4A7C59] text-sm font-semibold hover:bg-[#4A7C59]/10 transition"
              >
                <Mail className="w-4 h-4" /> Email {listing.host.name}
              </a>
            </div>

            {/* Post-booking share CTA. Sits between contact + Done as
                a soft, low-friction growth loop: guests who just paid
                often want to invite a friend with their dog to join.
                Each share carries the listing's first photo as og:image
                so iMessage / WhatsApp previews show the actual yard. */}
            <div className="mb-6 rounded-2xl border border-dashed border-[#4A7C59]/40 bg-[#4A7C59]/5 px-4 py-4">
              <p className="text-sm font-body text-[#2d2a26] mb-3 leading-snug">
                Bringing a friend with their pup?{" "}
                <span className="text-[#6b6560]">
                  Send them the listing — they&rsquo;ll see the yard photos + price.
                </span>
              </p>
              <button
                type="button"
                onClick={handleShare}
                data-testid="booking-confirmation-share"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white border-2 border-[#4A7C59] text-[#4A7C59] text-sm font-semibold hover:bg-[#4A7C59] hover:text-white transition-colors"
              >
                <Share2 className="w-4 h-4" /> Share with friends
              </button>
            </div>

            <button
              type="button"
              data-testid="booking-confirmation-done"
              onClick={onClose}
              className="px-6 py-2.5 rounded-full bg-[#2d2a26] text-white text-sm font-semibold hover:bg-black transition"
            >
              Done
            </button>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
            className="px-6 py-5 space-y-4"
          >
            {/* Host strip */}
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-[#e5e0d8]">
              <HostAvatar host={listing.host} size={44} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#2d2a26]">Message to {listing.host.name}</div>
                <div className="text-xs text-[#6b6560]">{listing.host.respondsIn}</div>
              </div>
            </div>

            {/* Fields */}
            <Field label="Your name *">
              <input
                data-testid="booking-name"
                value={form.guest_name}
                onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                required
                  className="w-full px-3 py-2 rounded-xl border border-[#e5e0d8] bg-white focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/40 focus:border-[#4A7C59] text-sm"
                  placeholder="Your name"
                />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Email *">
                  <input
                    data-testid="booking-email"
                    type="email"
                    value={form.guest_email}
                    onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                    required
                    className="w-full px-3 py-2 rounded-xl border border-[#e5e0d8] bg-white focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/40 focus:border-[#4A7C59] text-sm"
                    placeholder="you@example.com"
                  />
                </Field>
                <Field label="Phone (optional)">
                  <input
                    data-testid="booking-phone"
                    value={form.guest_phone}
                    onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-[#e5e0d8] bg-white focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/40 focus:border-[#4A7C59] text-sm"
                    placeholder="(555) 555-0123"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Date *">
                  <input
                    data-testid="booking-date"
                    type="date"
                    value={form.preferred_date}
                    onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                    required
                    className="w-full px-3 py-2 rounded-xl border border-[#e5e0d8] bg-white focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/40 focus:border-[#4A7C59] text-sm"
                  />
                </Field>
                <Field label="Time">
                  <input
                    data-testid="booking-time"
                    type="time"
                    value={form.preferred_time}
                    onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-[#e5e0d8] bg-white focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/40 focus:border-[#4A7C59] text-sm"
                  />
                </Field>
                <Field label="# Dogs">
                  <select
                    data-testid="booking-dogs"
                    value={form.dogs}
                    onChange={(e) => setForm({ ...form, dogs: parseInt(e.target.value, 10) })}
                    className="w-full px-3 py-2 rounded-xl border border-[#e5e0d8] bg-white focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/40 focus:border-[#4A7C59] text-sm"
                  >
                    {[1, 2, 3].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Message (optional)">
                <textarea
                  data-testid="booking-message"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#e5e0d8] bg-white focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/40 focus:border-[#4A7C59] text-sm resize-none"
                  placeholder={`Tell ${listing.host.name} about your dog(s) — breed, temperament, any special needs...`}
                />
              </Field>

            <div className="rounded-xl bg-[#fef3c7] border border-[#f5dca6] text-[#7c5a14] text-sm px-4 py-3 leading-relaxed">
              💰 ${listing.pricing.firstDogPerHour}/hr first dog, ${listing.pricing.additionalDogPerHour}/hr each additional —
              you&apos;ll pay {listing.host.name} directly. No membership, no credits, no hidden fees.
            </div>

            <div className="pt-1">
              <button
                type="submit"
                data-testid="booking-submit"
                disabled={!isValid || step === "submitting"}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#4A7C59] hover:bg-[#3d6749] disabled:bg-[#a8baaa] disabled:cursor-not-allowed text-white font-semibold transition"
              >
                {step === "submitting" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Send Booking Request
              </button>
              <p className="text-center text-xs text-[#6b6560] mt-2">
                {listing.host.name} will respond to your request directly
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ---- Tiny helpers ------------------------------------------------------
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold uppercase tracking-wider text-[#6b6560] mb-1.5">{label}</span>
    {children}
  </label>
);

const StarRow = ({ value, size = 14 }) => {
  const filled = Math.round(value);
  return (
    <span className="inline-flex items-center gap-px text-[#d4a017]" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} fill={i <= filled ? "currentColor" : "transparent"} stroke="currentColor" style={{ width: size, height: size }} />
      ))}
    </span>
  );
};

const HostAvatar = ({ host, size = 56 }) => {
  if (host.headshot) {
    return (
      <img loading="lazy"
        src={host.headshot}
        alt={host.name}
        style={{ width: size, height: size }}
        className="rounded-full object-cover bg-[#e5e0d8]"
      />
    );
  }
  // Fallback initial avatar in brand green.
  const initial = (host.name || "?")[0].toUpperCase();
  return (
    <span
      style={{ width: size, height: size, fontSize: size * 0.45 }}
      className="rounded-full bg-[#4A7C59] text-white font-heading font-bold flex items-center justify-center"
    >
      {initial}
    </span>
  );
};

// ---- Main page ---------------------------------------------------------
const PrivateParkDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const listing = useMemo(() => getPrivateParkBySlug(slug), [slug]);

  const [dogs, setDogs] = useState(1);
  const [hours, setHours] = useState(1);
  const [showModal, setShowModal] = useState(false);

  // SEO: page-specific title + description + canonical. Indexable
  // (no useNoIndex), per user requirement.
  useEffect(() => {
    if (!listing) return;
    document.title = listing.seo.title;
    const setMeta = (sel, attr, value) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, value);
    };
    const canonical = `https://pawground.app/private-parks/${listing.slug}`;
    setMeta('meta[name="description"]', "content", listing.seo.description);
    setMeta('link[rel="canonical"]', "href", canonical);
    setMeta('meta[property="og:title"]', "content", listing.seo.title);
    setMeta('meta[property="og:description"]', "content", listing.seo.description);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[property="og:image"]', "content", listing.photos[0]);
    setMeta('meta[name="twitter:title"]', "content", listing.seo.title);
    setMeta('meta[name="twitter:description"]', "content", listing.seo.description);
    // Remove any noindex left over from a previous page (e.g. /park/<id>).
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute("content", "index, follow");
  }, [listing]);

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="font-heading text-3xl text-[#2d2a26] mb-2">Listing not found</h1>
          <p className="text-[#6b6560] mb-6">We couldn&apos;t find that private park.</p>
          <Link to="/" className="text-[#4A7C59] font-semibold underline">
            Back home
          </Link>
        </div>
      </div>
    );
  }

  const { pricing } = listing;
  const extraDogs = Math.max(0, dogs - 1);
  const subtotalFirst = pricing.firstDogPerHour * hours;
  const subtotalExtras = pricing.additionalDogPerHour * extraDogs * hours;
  const total = subtotalFirst + subtotalExtras;

  // JSON-LD for SEO — LodgingBusiness with AggregateRating + Reviews so
  // the listing is eligible for rich results. We intentionally use
  // LodgingBusiness (not LocalBusiness) because that's the closest
  // Schema.org type to "rent this private space by the hour".
  const isNewListing = !listing.reviewCount || listing.reviewCount === 0;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    name: listing.name,
    url: `https://pawground.app/private-parks/${listing.slug}`,
    description: listing.about,
    image: listing.photos,
    address: {
      "@type": "PostalAddress",
      streetAddress: listing.address.split(",")[0],
      addressLocality: listing.city,
      addressRegion: listing.state,
      postalCode: (listing.address.match(/\d{5}/) || [""])[0],
      addressCountry: "US",
    },
    telephone: listing.host.phoneRaw,
    // AggregateRating + review[] only emitted when we actually have
    // reviews. Google's structured-data validator rejects
    // AggregateRating with reviewCount=0, and an empty review[] array
    // would trip the same warnings.
    ...(isNewListing
      ? {}
      : {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: listing.rating.toFixed(1),
            reviewCount: listing.reviewCount,
          },
          review: listing.reviews.map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.name },
            reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
            reviewBody: r.text,
          })),
        }),
    priceRange: `$${pricing.firstDogPerHour}/hr first dog, $${pricing.additionalDogPerHour}/hr each additional`,
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#2d2a26]">
      <JsonLd data={jsonLd} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-32 lg:pb-12">
        {/* Breadcrumbs */}
        <Breadcrumbs
          items={[
            { name: listing.city, to: `/${listing.citySlug}` },
            { name: "Parks", to: `/dog-parks/${listing.citySlug}` },
            { name: listing.name },
          ]}
        />

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mt-2">
          <span
            data-testid="private-park-badge"
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#4A7C59] text-white text-xs font-semibold"
          >
            🏡 Private Park
          </span>
          {listing.foundingHost && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#fef3c7] text-[#b45309] text-xs font-semibold">
              ⭐ Founding Host
            </span>
          )}
        </div>

        {/* Title + subtitle */}
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2d2a26] mt-3 leading-tight">
          {listing.name}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#6b6560]">
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-4 h-4" /> {listing.address}
          </span>
          <span className="hidden sm:inline">·</span>
          <span className="inline-flex items-center gap-1">
            <StarRow value={listing.rating} />
            <span className="font-semibold text-[#2d2a26] ml-1">{listing.rating.toFixed(1)}</span>
            {isNewListing ? (
              <span
                data-testid="new-listing-badge"
                className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.1em]"
                style={{ backgroundColor: "#E8F2EC", color: "#4A7C59" }}
              >
                New
              </span>
            ) : (
              <span>({listing.reviewCount} reviews)</span>
            )}
          </span>
        </div>

        {/* Body */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 lg:gap-10">
          {/* Left column */}
          <div className="min-w-0">
            <Carousel photos={listing.photos} alt={listing.name} />

            {/* Tagline */}
            <div className="mt-5 px-4 py-3 rounded-2xl bg-[#4A7C59]/8 border border-[#4A7C59]/20">
              <p className="italic text-[#4A7C59] text-base sm:text-lg">{listing.tagline}</p>
            </div>

            {/* Highlights */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {listing.highlights.map((h) => (
                <div
                  key={h.label}
                  className="rounded-2xl bg-white border border-[#e5e0d8] p-4 flex gap-3"
                >
                  <span className="text-2xl shrink-0" aria-hidden>
                    {h.emoji}
                  </span>
                  <div>
                    <div className="font-semibold text-[#2d2a26]">{h.label}</div>
                    <div className="text-sm text-[#6b6560] mt-0.5 leading-relaxed">{h.detail}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* About */}
            <Section title="About this spot">
              <p className="text-[#4a4640] leading-relaxed">{listing.about}</p>
            </Section>

            {/* Amenities */}
            <Section title="Amenities">
              <div className="flex flex-wrap gap-2">
                {listing.amenities.map((a) => (
                  <span
                    key={a}
                    className="inline-flex items-center px-3 py-1.5 rounded-full bg-white border border-[#e5e0d8] text-sm text-[#2d2a26]"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </Section>

            {/* Availability */}
            <Section title="Availability">
              <div className="rounded-2xl bg-white border border-[#e5e0d8] p-4 flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#4A7C59] mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-[#2d2a26]">{listing.availability.headline}</div>
                  {listing.availability.sameDay && (
                    <div className="mt-1 text-sm font-medium text-[#4A7C59]">⚡ Same-day bookings welcome!</div>
                  )}
                </div>
              </div>
            </Section>

            {/* Rules */}
            <Section title="House rules">
              <ul className="space-y-2">
                {listing.rules.map((r) => (
                  <li key={r} className="flex items-start gap-2 text-[#4a4640] leading-relaxed">
                    <Check className="w-4 h-4 text-[#4A7C59] mt-1 shrink-0" strokeWidth={3} />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </Section>

            {/* Reviews. Hidden entirely on brand-new listings — showing
                an empty "Reviews (0)" section reads as broken. Once
                reviews land, this auto-renders again. */}
            {!isNewListing && (
              <Section title={`Reviews (${listing.reviewCount})`}>
                <div className="space-y-3">
                  {listing.reviews.map((r) => (
                    <div
                      key={`${r.name}-${r.when}`}
                      className="rounded-2xl bg-white border border-[#e5e0d8] p-4"
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-semibold text-[#2d2a26]">{r.name}</span>
                        <span className="text-xs text-[#6b6560]">{r.when}</span>
                      </div>
                      <StarRow value={r.rating} />
                      <p className="text-[#4a4640] leading-relaxed mt-2">{r.text}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Host */}
            <Section title="Your host">
              <div className="rounded-2xl bg-white border border-[#e5e0d8] p-5">
                <div className="flex items-center gap-4">
                  <HostAvatar host={listing.host} size={64} />
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold text-xl text-[#2d2a26]">{listing.host.name}</span>
                      {listing.foundingHost && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#b45309] text-[11px] font-semibold">
                          Founding Host
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#6b6560]">{listing.host.respondsIn}</div>
                  </div>
                </div>
                <p className="text-[#4a4640] leading-relaxed mt-4">{listing.host.bio}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <a
                    href={`tel:${listing.host.phoneRaw}`}
                    data-testid="host-phone"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4A7C59] hover:bg-[#3d6749] text-white text-sm font-semibold transition"
                  >
                    <Phone className="w-4 h-4" /> {listing.host.phone}
                  </a>
                  <a
                    href={`mailto:${listing.host.email}`}
                    data-testid="host-email"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#4A7C59] text-[#4A7C59] hover:bg-[#4A7C59]/10 text-sm font-semibold transition"
                  >
                    <Mail className="w-4 h-4" /> Email {listing.host.name}
                  </a>
                </div>
              </div>
            </Section>
          </div>

          {/* Right column — sticky booking */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <BookingPanel
                listing={listing}
                dogs={dogs}
                hours={hours}
                setDogs={setDogs}
                setHours={setHours}
                total={total}
                subtotalFirst={subtotalFirst}
                subtotalExtras={subtotalExtras}
                extraDogs={extraDogs}
                onBook={() => setShowModal(true)}
              />
            </div>
          </aside>
        </div>

        {/* Mobile sticky bottom bar */}
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-[90] bg-white border-t border-[#e5e0d8] shadow-[0_-4px_20px_rgba(0,0,0,0.06)] px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-heading text-xl font-bold text-[#2d2a26] leading-none">
              ${pricing.firstDogPerHour}
              <span className="text-xs font-normal text-[#6b6560] ml-1">/hr first dog</span>
            </div>
            <div className="flex items-center gap-1 mt-1 text-xs text-[#6b6560]">
              <StarRow value={listing.rating} size={12} />
              <span className="font-semibold text-[#2d2a26]">{listing.rating.toFixed(1)}</span>
              {isNewListing ? (
                <span
                  className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
                  style={{ backgroundColor: "#E8F2EC", color: "#4A7C59" }}
                >
                  New
                </span>
              ) : (
                <span>({listing.reviewCount})</span>
              )}
            </div>
          </div>
          <button
            type="button"
            data-testid="mobile-book-now"
            onClick={() => setShowModal(true)}
            className="px-5 py-2.5 rounded-full bg-[#4A7C59] hover:bg-[#3d6749] text-white font-semibold text-sm transition"
          >
            Book Now
          </button>
        </div>

        {showModal && (
          <BookingModal
            listing={listing}
            dogs={dogs}
            hours={hours}
            total={total}
            onClose={() => setShowModal(false)}
          />
        )}
      </div>
    </div>
  );
};

// Small section wrapper — heading with underline matches site rhythm.
const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#2d2a26] pb-2 border-b border-[#e5e0d8] mb-4">
      {title}
    </h2>
    {children}
  </section>
);

// Right-column / desktop booking panel.
const BookingPanel = ({
  listing,
  dogs,
  hours,
  setDogs,
  setHours,
  total,
  subtotalFirst,
  subtotalExtras,
  extraDogs,
  onBook,
}) => {
  const { pricing } = listing;
  return (
    <div
      data-testid="booking-panel"
      className="rounded-3xl bg-white border border-[#e5e0d8] shadow-[0_6px_30px_rgba(45,42,38,0.06)] p-5"
    >
      <div className="flex items-baseline gap-2">
        <span className="font-heading text-3xl font-bold text-[#2d2a26]">${pricing.firstDogPerHour}</span>
        <span className="text-sm text-[#6b6560]">first dog / hour</span>
      </div>
      <div className="text-xs text-[#6b6560] mt-0.5">+${pricing.additionalDogPerHour} each additional dog / hour</div>
      <div className="flex items-center gap-1 mt-1 text-xs">
        <StarRow value={listing.rating} size={12} />
        <span className="font-semibold text-[#2d2a26] ml-1">{listing.rating.toFixed(1)}</span>
        <span className="text-[#6b6560]">({listing.reviewCount} reviews)</span>
      </div>

      <div className="mt-4 border-t border-[#f0ebe1] pt-1">
        <Stepper label="Dogs" value={dogs} min={1} max={pricing.maxDogs} onChange={setDogs} testId="stepper-dogs" />
        <div className="border-t border-[#f0ebe1]" />
        <Stepper label="Hours" value={hours} min={1} max={pricing.maxHours} onChange={setHours} testId="stepper-hours" />
      </div>

      <div className="mt-3 text-sm text-[#6b6560] space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span>
            ${pricing.firstDogPerHour} × 1st dog × {hours} hr{hours !== 1 ? "s" : ""}
          </span>
          <span className="text-[#2d2a26] font-medium">${subtotalFirst.toFixed(2)}</span>
        </div>
        {extraDogs > 0 && (
          <div className="flex items-center justify-between gap-2">
            <span>
              ${pricing.additionalDogPerHour} × {extraDogs} extra dog{extraDogs !== 1 ? "s" : ""} × {hours} hr{hours !== 1 ? "s" : ""}
            </span>
            <span className="text-[#2d2a26] font-medium">${subtotalExtras.toFixed(2)}</span>
          </div>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-[#f0ebe1] flex items-center justify-between">
        <span className="text-sm font-semibold text-[#2d2a26]">Total</span>
        <span data-testid="booking-total" className="font-heading text-2xl font-bold text-[#2d2a26]">
          ${total.toFixed(2)}
        </span>
      </div>

      <button
        type="button"
        data-testid="book-this-spot"
        onClick={onBook}
        className="mt-4 w-full px-6 py-3 rounded-full bg-[#4A7C59] hover:bg-[#3d6749] text-white font-semibold transition"
      >
        Book This Spot
      </button>
      <p className="text-center text-xs text-[#6b6560] mt-2">
        No membership required · Pay the host directly
      </p>
    </div>
  );
};

export default PrivateParkDetail;
